/**
 * Dettaglio di una ricerca (SPEC-015 §8): avanzamento mentre gira, poi le
 * card dei risultati.
 *
 * Il polling si ferma da solo a run finito (vedi `useDiscoveryRun`): una
 * pagina lasciata aperta non deve continuare a interrogare il server.
 */

import { Link, useNavigate, useParams } from 'react-router-dom';
import { useState } from 'react';
import { Alert, Badge, Button, Card, DomainBadge, EmptyState, Spinner } from '@/ui';
import { formatDate, formatDateTime } from '@/lib/format';
import { ApiError } from '@/lib/api-client';
import { STATUS_LABEL, STATUS_TONE } from './discovery-labels';
import { DiscoveryResultCard } from './DiscoveryResultCard';
import { RunProgressPanel } from './RunProgressPanel';
import { RotationConfirmModal } from './RotationConfirmModal';
import {
  useDeleteDiscoveryRun,
  useDiscoveryRun,
  useRefreshRun,
  useRunRotations,
  useStartRotation,
  type DiscoveryResult,
  type DiscoveryRun,
} from './api';
import { ComparisonCard } from './ComparisonCard';
import { RefreshRunModal } from './RefreshRunModal';
import './discovery.css';

function errorMessage(error: unknown): string {
  if (error instanceof ApiError) return error.message;
  return error instanceof Error ? error.message : 'Errore imprevisto';
}

export function DiscoveryRunPage() {
  const { runId } = useParams<{ runId: string }>();
  const navigate = useNavigate();
  const run = useDiscoveryRun(runId!);
  const deleteRun = useDeleteDiscoveryRun();
  const refresh = useRefreshRun(runId!);
  const [refreshing, setRefreshing] = useState(false);

  if (run.isLoading) return <Spinner label="Carico la ricerca…" />;
  if (run.isError || !run.data) {
    return (
      <div className="page">
        <EmptyState
          icon="🔎"
          title="Ricerca non trovata"
          description="Non esiste, oppure non è una delle tue."
          action={<Button onClick={() => navigate('/discovery')}>Torna a Scopri</Button>}
        />
      </div>
    );
  }

  const data = run.data;
  const running = data.status === 'queued' || data.status === 'running';

  return (
    <div className="page">
      <header className="page__head">
        <div>
          <h1>{data.themeTitle}</h1>
          {data.themeDescription && <p className="page__subtitle">{data.themeDescription}</p>}
          <p className="cell--minor">Avviata {formatDateTime(data.createdAt)}</p>
        </div>
        <div className="discovery-detail__head-actions">
          <DomainBadge domain={data.domain} />
          <Badge tone={STATUS_TONE[data.status]}>{STATUS_LABEL[data.status]}</Badge>
          {/* F-140: la stessa domanda, settimane dopo. Solo su una ricerca conclusa. */}
          {data.status === 'completed' && (
            <Button variant="secondary" onClick={() => setRefreshing(true)}>
              Aggiorna ricerca
            </Button>
          )}
          <Button
            variant="ghost"
            onClick={() => {
              // La cancellazione toglie anche la visibilità dei titoli
              // ottenuta da questa ricerca: va detto prima, non scoperto dopo.
              if (!window.confirm('Elimini questa ricerca? I titoli scoperti resteranno raggiungibili solo se li hai aggiunti alla watchlist.')) return;
              deleteRun.mutate(data.id, { onSuccess: () => navigate('/discovery') });
            }}
            disabled={deleteRun.isPending}
          >
            Elimina
          </Button>
        </div>
      </header>

      {refreshing && (
        <RefreshRunModal
          run={data}
          pending={refresh.isPending}
          error={refresh.error ? errorMessage(refresh.error) : null}
          onClose={() => setRefreshing(false)}
          onConfirm={(input) =>
            refresh.mutate(input, {
              onSuccess: (child) => {
                setRefreshing(false);
                navigate(`/discovery/${child.id}`);
              },
            })
          }
        />
      )}

      <Lineage run={data} />

      {running && (
        <Card>
          <div className="discovery-progress">
            <Spinner label={data.progress ?? 'In corso…'} />
            <p className="cell--minor">
              La ricerca continua anche se chiudi la pagina: puoi tornare più tardi.
            </p>
          </div>
          {/* F-084: il dettaglio compare solo se il server l'ha costruito, cioè
              solo mentre la ricerca è viva. Lo spinner sopra resta comunque:
              dice CHE sta lavorando, questo dice COSA ha fatto finora. */}
          {data.progressDetail && (
            <RunProgressPanel progress={data.progressDetail} current={data.progress} />
          )}
        </Card>
      )}

      {data.status === 'failed' && (
        <Alert variant="error" title="La ricerca non è riuscita">
          {data.errorMessage ?? 'Errore non specificato.'}
        </Alert>
      )}

      {data.status === 'completed' && data.emptyReason && (
        <Alert variant="warning" title="Nessun titolo trovato">
          {data.emptyReason}
        </Alert>
      )}

      {data.scoringDegraded && (
        <Alert variant="warning" title="Motivazioni non elaborate">
          I titoli sono stati identificati, ma la valutazione finale non è riuscita: qui sotto trovi
          le evidenze raccolte, senza punteggio.
        </Alert>
      )}

      {/* Il confronto sta PRIMA della rotazione e dei risultati: e' la risposta
          alla domanda per cui si e' aggiornata la ricerca. */}
      {data.status === 'completed' && data.parent && (
        <ComparisonCard runId={data.id} parentRunId={data.parent.id} parentCreatedAt={data.parent.createdAt} />
      )}

      {data.status === 'completed' && <RotationSection runId={data.id} results={data.results} />}

      {data.results.length > 0 && (
        <>
          {data.stats && (
            <p className="cell--minor discovery-detail__stats">
              {data.stats.candidatesFound ?? 0} nomi trovati sul web · {data.stats.resolved ?? 0} identificati
              {data.stats.dropped ? ` · ${data.stats.dropped} esclusi dai filtri` : ''}
            </p>
          )}
          <div className="discovery-results">
            {data.results.map((result) => (
              <DiscoveryResultCard key={result.id} runId={data.id} result={result} />
            ))}
          </div>
        </>
      )}

      {data.status === 'completed' && data.results.length === 0 && !data.emptyReason && (
        <EmptyState icon="🔎" title="Nessun risultato" description="La ricerca si è chiusa senza titoli." />
      )}
    </div>
  );
}

/**
 * La discendenza di una ricerca (F-140): da qualunque anello della catena si
 * vedono quello prima e quelli dopo. E' la tracciabilita' lato utente, e
 * non e' decorazione — senza, due ricerche sullo stesso tema a un mese di
 * distanza sarebbero due voci indistinguibili nell'elenco.
 */
function Lineage({ run }: { run: DiscoveryRun }) {
  if (!run.parent && run.refreshes.length === 0) return null;
  return (
    <Alert variant="info" title={run.parent ? 'Questa è una ricerca aggiornata' : 'Questa ricerca è stata aggiornata'}>
      {run.parent && (
        <p>
          È la stessa domanda della{' '}
          <Link to={`/discovery/${run.parent.id}`}>ricerca del {formatDate(run.parent.createdAt)}</Link>, rifatta
          più tardi.
          {run.refreshOptions?.autoRotate && (
            <>
              {' '}
              Al termine parte da sola la rotazione generale
              {run.refreshOptions.wishlistOptions ? ', con inserimento in wishlist' : ''}: lo hai deciso tu
              all&apos;avvio.
            </>
          )}
        </p>
      )}
      {run.refreshes.length > 0 && (
        <p>
          Aggiornamenti successivi:{' '}
          {run.refreshes.map((child, index) => (
            <span key={child.id}>
              {index > 0 && ' · '}
              <Link to={`/discovery/${child.id}`}>{formatDate(child.createdAt)}</Link>
              {child.status !== 'completed' && ` (${STATUS_LABEL[child.status as keyof typeof STATUS_LABEL] ?? child.status})`}
            </span>
          ))}
        </p>
      )}
    </Alert>
  );
}

/**
 * Rotazione generale: elegge un titolo ogni cinque fra quelli identificati e
 * lo mette in watchlist.
 *
 * Il pulsante resta **visibile** anche quando non è utilizzabile, con la
 * ragione accanto: una funzione invisibile non si può nemmeno desiderare
 * (stessa scelta del «Resoconto ora» di F-051).
 */
function RotationSection({ runId, results }: { runId: string; results: DiscoveryResult[] }) {
  const rotations = useRunRotations(runId);
  const start = useStartRotation(runId);
  const [confirming, setConfirming] = useState(false);
  const identified = results.filter((result) => result.resolutionStatus === 'resolved').length;
  const running = (rotations.data?.items ?? []).some(
    (rotation) => rotation.status === 'queued' || rotation.status === 'running',
  );
  // Sotto i due titoli non c'è niente da confrontare: eleggerne uno solo
  // sarebbe promuoverlo senza averlo messo accanto a nessun altro.
  const enough = identified >= 2;

  return (
    <Card title="Rotazione generale">
      <p className="rotation-intro">
        Confronta i titoli identificati <strong>a gruppi di cinque</strong> e ne elegge uno per gruppo —
        quello con più potenziale <em>da osservare</em>, non da comprare — aggiungendolo alla watchlist
        con il motivo e il tema di partenza.
      </p>
      <p className="cell--minor">
        {identified} titoli identificati su {results.length}
        {enough ? ` · ${Math.max(1, Math.floor(identified / 5) || 1)} circa da eleggere` : ''}
      </p>

      {!enough && (
        <Alert variant="info" title="Servono almeno due titoli identificati">
          Identifica i titoli incerti qui sotto: la rotazione può scegliere solo fra quelli collegati a
          un titolo quotato.
        </Alert>
      )}

      <div className="rotation-actions">
        {/* Apre una conferma e non lancia: la rotazione SCRIVE — watchlist, e
            volendo wishlist con dei limiti — e sono conseguenze da vedere
            prima, non da scoprire dopo. */}
        <Button
          onClick={() => setConfirming(true)}
          disabled={!enough || running}
          title={!enough ? 'Servono almeno due titoli identificati' : undefined}
        >
          {running ? 'Rotazione in corso…' : 'Avvia rotazione generale'}
        </Button>
      </div>

      {confirming && (
        <RotationConfirmModal
          identified={identified}
          pending={start.isPending}
          error={start.isError ? (start.error instanceof Error ? start.error.message : 'Errore imprevisto.') : null}
          onConfirm={(input) => start.mutate(input, { onSuccess: () => setConfirming(false) })}
          onClose={() => setConfirming(false)}
        />
      )}

      {(rotations.data?.items ?? []).length > 0 && (
        <ul className="discovery-runs">
          {rotations.data!.items.map((rotation) => (
            <li key={rotation.id} className="discovery-runs__item">
              <Link to={`/discovery/rotations/${rotation.id}`} className="discovery-runs__link">
                <span className="discovery-runs__title">
                  {rotation.status === 'completed'
                    ? `${rotation.picksTotal} titoli eletti su ${rotation.groupsTotal} gruppi`
                    : (rotation.progress ?? 'In attesa')}
                </span>
                <span className="cell--minor">{formatDateTime(rotation.createdAt)}</span>
              </Link>
              <Badge tone={rotation.status === 'completed' ? 'positive' : rotation.status === 'failed' ? 'negative' : 'accent'}>
                {rotation.status === 'completed' ? 'Completata' : rotation.status === 'failed' ? 'Non riuscita' : 'In corso'}
              </Badge>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}
